import { useState, useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { ArrowLeft, ChevronRight, LayoutGrid, List, MessageCircle, Pencil, Search, Trash2 } from 'lucide-react'
import ResponsiveShell from '../components/layout/ResponsiveShell'
import { useLangPrefix } from '../hooks/useLangPrefix'
import useManagerStore from '../store/managerStore'
import { patchContact, deleteContact, getContact } from '../api/contacts'
import ContactActionsBar from '../components/contacts/ContactActionsBar'
import ContactBookingHistory from '../components/contacts/ContactBookingHistory'
import ContactChannelBadge from '../components/contacts/ContactChannelBadge'
import ContactTagsPanel from '../components/contacts/ContactTagsPanel'
import ImportContactsModal from '../components/contacts/ImportContactsModal'
import ExportContactsButton from '../components/contacts/ExportContactsButton'
import { canAccess, RBAC_MODULES } from '../lib/rbac'
import useInfiniteContacts from '../hooks/useInfiniteContacts'
import { getUniqueContactChannels } from '../utils/contactChannels'
import ContactDobField from '../components/contacts/ContactDobField'
import ContactBookingSummary from '../components/contacts/ContactBookingSummary'
import { formatMoney } from '../lib/money'
import { useTenantCurrencyState } from '../hooks/useTenantCurrency'
import {
  formatContactDob,
  formatContactGender,
  normalizeDobForCompare,
  toDobApiValue,
  toDobDisplayValue,
} from '../utils/contactFields'
import {
  PHONE_INPUT_MAX_LENGTH,
  PHONE_VALIDATION_MESSAGE,
  isValidPhoneNumber,
  normalizePhoneNumber,
  sanitizePhoneInput,
} from '../utils/phone'

function formatDate(dateStr) {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('vi-VN')
}

function InfoRow({ label, value }) {
  return (
    <div className="flex gap-2 mb-2 text-sm">
      <span className="text-gray-400 w-28 flex-shrink-0">{label}</span>
      <span className="font-semibold text-gray-800">{value}</span>
    </div>
  )
}

function FollowerGridCard({ follower, onClick }) {
  const visibleChannels = getUniqueContactChannels(follower.channels)

  return (
    <button
      onClick={onClick}
      className="bg-white rounded-2xl p-3 flex flex-col items-center gap-2 cursor-pointer active:scale-[0.99] transition-transform w-full"
      style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
    >
      <div className="w-12 h-12 rounded-full bg-[#e3eaf8] flex items-center justify-center text-[#1535c8] text-base font-bold flex-shrink-0 overflow-hidden">
        {follower.avatar_url
          ? <img src={follower.avatar_url} alt={follower.name || 'U'} className="w-full h-full object-cover" />
          : (follower.name || 'U').charAt(0).toUpperCase()
        }
      </div>
      <div className="w-full text-center">
        <div className="text-sm font-bold text-gray-900 truncate">{follower.name || 'Unknown'}</div>
        {follower.phone && (
          <div className="text-xs text-gray-400 truncate">{follower.phone}</div>
        )}
        {visibleChannels.length > 0 && (
          <div className="flex flex-wrap gap-1 justify-center mt-1">
            {visibleChannels.map((ch, i) => (
              <ContactChannelBadge key={i} platform={ch.platform} />
            ))}
          </div>
        )}
        <ContactBookingSummary summary={follower.booking_summary} centered />
      </div>
    </button>
  )
}

function FollowerStatTile({ label, value }) {
  return (
    <div className="flex-1 bg-gray-50 rounded-md px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-sm font-semibold text-gray-800 mt-0.5">{value}</div>
    </div>
  )
}

function FollowerEditForm({ draft, setDraft, t }) {
  const label = (key, fallback) => t?.(key, { defaultValue: fallback }) ?? fallback
  const field = (key, labelKey, fallback, type = 'text') => (
    <div className="flex flex-col gap-1 mb-3">
      <label className="text-xs text-gray-500">{label(labelKey, fallback)}</label>
      {type === 'textarea' ? (
        <textarea
          className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={3}
          value={draft[key] ?? ''}
          onChange={e => setDraft({ ...draft, [key]: e.target.value })}
        />
      ) : (
        <input
          type={type}
          className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={draft[key] ?? ''}
          onChange={e => {
            const nextValue = key === 'phone'
              ? sanitizePhoneInput(e.target.value)
              : e.target.value
            setDraft({ ...draft, [key]: nextValue })
          }}
          inputMode={key === 'phone' ? 'tel' : undefined}
          maxLength={key === 'phone' ? PHONE_INPUT_MAX_LENGTH : undefined}
        />
      )}
    </div>
  )

  return (
    <div className="mt-2">
      {field('name',    'follower.name',    'Name')}
      {field('phone',   'follower.phone',   'Phone', 'tel')}
      {field('email',   'follower.email',   'Email', 'email')}
      {field('address',  'follower.address',  'Address')}
      {field('street',   'contacts.street',   'Street / house number')}
      {field('ward',     'contacts.ward',     'Ward')}
      {field('district', 'contacts.district', 'District')}
      {field('province', 'contacts.province', 'Province / City')}
      {field('latitude',  'contacts.latitude',  'Latitude')}
      {field('longitude', 'contacts.longitude', 'Longitude')}
      <div className="flex flex-col gap-1 mb-3">
        <label className="text-xs text-gray-500">{label('contacts.gender', 'Gender')}</label>
        <select
          className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={draft.gender ?? ''}
          onChange={e => setDraft({ ...draft, gender: e.target.value })}
        >
          <option value="">{label('contacts.emptyValue', '—')}</option>
          <option value="male">{label('contacts.genderValues.male', 'Male')}</option>
          <option value="female">{label('contacts.genderValues.female', 'Female')}</option>
        </select>
      </div>
      <ContactDobField
        label={label('contacts.dob', 'Date of birth')}
        placeholder={label('contacts.dobPlaceholder', 'DD/MM/YYYY')}
        value={draft.dob}
        onChange={next => setDraft({ ...draft, dob: next })}
      />
      <div className="flex flex-col gap-1 mb-3">
        <label className="text-xs text-gray-500">{label('follower.stage', 'Stage')}</label>
        <select
          className="border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          value={draft.stage ?? 'follower'}
          onChange={e => setDraft({ ...draft, stage: e.target.value })}
        >
          <option value="follower">{label('follower.stageFollower', 'Follower')}</option>
          <option value="customer">{label('follower.stageCustomer', 'Customer')}</option>
        </select>
      </div>
      {field('notes', 'follower.notes', 'Notes', 'textarea')}
    </div>
  )
}

export default function FollowersPage() {
  const { t, i18n } = useTranslation()
  // Totals wait for a resolved tenant currency: while loading, on error or
  // without a tenant the hook only has the VND placeholder.
  const { currency, isResolved: currencyResolved } = useTenantCurrencyState()
  const navigate = useNavigate()
  const { prefix } = useLangPrefix()
  const { tenantId: storedTenantId, role } = useManagerStore()
  const tenantId = storedTenantId || ''
  const canViewBookingHistory = canAccess(role, RBAC_MODULES.bookings)

  const [selectedFollower, setSelectedFollower] = useState(null)
  const [showDetail, setShowDetail]             = useState(false)
  const [searchQuery, setSearchQuery]           = useState('')
  const [importOpen, setImportOpen]             = useState(false)
  const [viewMode, setViewMode]                 = useState(() => localStorage.getItem('growchat.followers.viewMode') || 'list')
  const [editMode, setEditMode]                 = useState(false)
  const [editDraft, setEditDraft]               = useState(null)
  const [saving, setSaving]                     = useState(false)
  const [deleting, setDeleting]                 = useState(false)
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [contactDetailLoading, setContactDetailLoading] = useState(false)
  const {
    contacts: followers,
    setContacts: setFollowers,
    loading: contactsLoading,
    hasMore,
    error: contactsError,
    refresh: loadFollowers,
    loadMoreRef,
  } = useInfiniteContacts({ tenantId, stage: 'follower', search: searchQuery })

  useEffect(() => {
    localStorage.setItem('growchat.followers.viewMode', viewMode)
  }, [viewMode])

  async function loadFollowerDetail(contactId) {
    if (!tenantId || !contactId) return
    setContactDetailLoading(true)
    try {
      const detail = await getContact(tenantId, contactId)
      const mergeDetail = current => {
        if (!current || current.id !== contactId) return current
        return {
          ...current,
          ...detail,
          name: detail.name || current.name || 'Unknown',
          phone: detail.phone ?? current.phone ?? '',
          email: detail.email ?? current.email ?? '',
          bookings: detail.bookings ?? current.bookings ?? [],
        }
      }
      setSelectedFollower(mergeDetail)
      setFollowers(prev => prev.map(follower => (
        follower.id === contactId ? mergeDetail(follower) : follower
      )))
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'get_contact_detail' },
        extra: { tenantId, contactId },
      })
    } finally {
      setContactDetailLoading(false)
    }
  }

  function openDetail(follower) {
    setSelectedFollower(follower)
    setShowDetail(true)
    loadFollowerDetail(follower.id)
  }

  function closeDetail() {
    setShowDetail(false)
    setSelectedFollower(null)
    setEditMode(false)
    setEditDraft(null)
    setSaving(false)
    setDeleting(false)
    setDeleteConfirmOpen(false)
    setContactDetailLoading(false)
  }

  function startEdit() {
    if (!selectedFollower) return
    setEditDraft({
      name:    selectedFollower.name    ?? '',
      phone:   selectedFollower.phone   ?? '',
      email:   selectedFollower.email   ?? '',
      address: selectedFollower.address ?? '',
      street:   selectedFollower.street   ?? '',
      ward:     selectedFollower.ward     ?? '',
      district: selectedFollower.district ?? '',
      province: selectedFollower.province ?? '',
      latitude:  selectedFollower.latitude  ?? '',
      longitude: selectedFollower.longitude ?? '',
      stage:   selectedFollower.stage   ?? 'follower',
      notes:   selectedFollower.notes   ?? '',
      gender:  selectedFollower.gender  ?? '',
      dob:     toDobDisplayValue(selectedFollower.dob),
    })
    setEditMode(true)
  }

  function cancelEdit() {
    setEditMode(false)
    setEditDraft(null)
  }

  function updateSelectedFollowerTags(next) {
    if (!selectedFollower?.id) return
    setSelectedFollower(f => (f ? { ...f, tags: next } : f))
    setFollowers(prev => prev.map(f =>
      f.id === selectedFollower.id ? { ...f, tags: next } : f
    ))
  }

  async function saveEdit() {
    if (!selectedFollower?.id || !editDraft) return
    const nextPhone = normalizePhoneNumber(editDraft.phone)
    if (nextPhone && !isValidPhoneNumber(nextPhone)) {
      alert(PHONE_VALIDATION_MESSAGE)
      return
    }
    setSaving(true)
    try {
      const patch = {}
      const fields = ['name', 'phone', 'email', 'address', 'street', 'ward', 'district', 'province', 'latitude', 'longitude', 'stage', 'notes', 'gender']
      const numericFields = ['latitude', 'longitude']
      for (const f of fields) {
        const current = f === 'phone'
          ? normalizePhoneNumber(selectedFollower[f])
          : (selectedFollower[f] ?? '').toString().trim()
        const next = f === 'phone'
          ? nextPhone
          : (editDraft[f] ?? '').trim()
        if (next !== current) {
          if (numericFields.includes(f)) {
            if (next !== '' && Number.isNaN(Number(next))) {
              alert(`${f} must be a number`)
              return
            }
            patch[f] = next === '' ? null : Number(next)
          } else {
            patch[f] = next === '' ? null : next
          }
        }
      }
      const nextDobDisplay = (editDraft.dob ?? '').trim()
      const currentDob = normalizeDobForCompare(selectedFollower.dob)
      if (nextDobDisplay !== toDobDisplayValue(selectedFollower.dob)) {
        if (!nextDobDisplay) {
          if (currentDob) patch.dob = null
        } else {
          const nextDobApi = toDobApiValue(nextDobDisplay)
          if (!nextDobApi) {
            alert(t?.('contacts.dobInvalid') || 'Invalid date of birth. Use DD/MM/YYYY.')
            return
          }
          if (nextDobApi !== currentDob) patch.dob = nextDobApi
        }
      }
      if (Object.keys(patch).length === 0) {
        setEditMode(false)
        setEditDraft(null)
        return
      }

      const updated = await patchContact(tenantId, selectedFollower.id, patch)
      setSelectedFollower(current => current?.id === selectedFollower.id
        ? {
            ...current,
            ...(updated || {}),
            booking_summary: updated?.booking_summary ?? current.booking_summary,
            bookings: updated?.bookings ?? current.bookings ?? [],
            tags: updated?.tags ?? current.tags,
          }
        : updated
      )
      setEditMode(false)
      setEditDraft(null)
      loadFollowers()
    } catch (err) {
      Sentry.captureException(err, { tags: { action: 'patch_contact' } })
      alert(t?.('errors.saveFailed') || 'Failed to save changes')
    } finally {
      setSaving(false)
    }
  }

  async function deleteFollower() {
    if (!tenantId || !selectedFollower?.id || deleting) return

    const contactId = selectedFollower.id
    setDeleting(true)
    try {
      await deleteContact(tenantId, contactId)
      setFollowers(prev => prev.filter(follower => follower.id !== contactId))
      toast.success(t?.('followers.deleteSuccess') || 'Delete follower success')
      closeDetail()
    } catch (err) {
      Sentry.captureException(err, {
        tags: { action: 'delete_follower' },
        extra: { tenantId, contactId },
      })
      toast.error(t?.('followers.deleteFailed') || t?.('common.requestFailed') || 'Request failed')
    } finally {
      setDeleting(false)
    }
  }

  // Derived flags for read-mode sections
  const hasContactInfo = f =>
    f.phone || f.email || f.address || f.street || f.ward || f.district || f.province ||
    f.latitude != null || f.longitude != null || f.stage || f.gender || f.dob
  return (
    <>
      <ResponsiveShell title={t('followers.title')}>
        <div className="bg-[#f2f4f8] pb-[10px]">
          <div className="sticky top-0 bg-[#f2f4f8] pt-2 pb-3 z-20">
            <div className="bg-white rounded-xl px-3 py-2.5 flex items-center gap-2" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <Search size={14} className="text-zinc-400" />
              <input
                className="flex-1 text-sm text-gray-800 placeholder-gray-400 outline-none border-none bg-transparent"
                placeholder={t('followers.searchPlaceholder')}
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            <div className="flex flex-wrap items-center justify-between mt-2 gap-2">
              <ContactActionsBar
                tenantId={tenantId}
                defaultStage="follower"
                onContactCreated={loadFollowers}
                onImportClick={() => setImportOpen(true)}
              />
              <div className="flex items-center gap-2">
                <ExportContactsButton
                  tenantId={tenantId}
                  stage="follower"
                  search={searchQuery}
                  filenamePrefix="followers"
                />
                <div className="flex border border-slate-200 rounded-md">
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-1.5 rounded-l-md transition-colors ${viewMode === 'list' ? 'bg-[#e3eaf8] text-[#1535c8]' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    <List size={15} />
                  </button>
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-1.5 rounded-r-md transition-colors ${viewMode === 'grid' ? 'bg-[#e3eaf8] text-[#1535c8]' : 'text-slate-500 hover:bg-slate-50'}`}
                  >
                    <LayoutGrid size={15} />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {followers.length === 0 && !contactsLoading && !contactsError ? (
            <div className="bg-white rounded-2xl p-8 text-center text-gray-400 text-sm mt-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              {t('followers.noFollowers')}
            </div>
          ) : viewMode === 'list' ? (
            <div>
              {followers.map(follower => (
                <button
                  key={follower.id}
                  className="w-full text-left bg-white rounded-2xl p-4 mb-2.5 flex items-start gap-3 cursor-pointer active:scale-[0.99] transition-transform"
                  style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
                  onClick={() => openDetail(follower)}
                >
                  <div className="w-11 h-11 rounded-full bg-[#e3eaf8] flex items-center justify-center text-[#1535c8] text-base font-bold flex-shrink-0 overflow-hidden">
                    {follower.avatar_url
                      ? <img src={follower.avatar_url} alt={follower.name || 'U'} className="w-full h-full object-cover" />
                      : (follower.name || 'U').charAt(0).toUpperCase()
                    }
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-bold text-gray-900">{follower.name || 'Unknown'}</div>
                    {follower.phone && (
                      <div className="text-xs text-gray-400">{follower.phone}</div>
                    )}
                    <div className="flex gap-1.5 flex-wrap mt-1">
                      {getUniqueContactChannels(follower.channels).map((ch, i) => (
                        <ContactChannelBadge key={i} platform={ch.platform} />
                      ))}
                    </div>
                    <div className="text-xs text-gray-400 mt-1">
                      {t('followers.followedAt')}{' '}
                      {follower.created_at ? formatDate(follower.created_at) : '—'}
                    </div>
                    <ContactBookingSummary summary={follower.booking_summary} />
                  </div>
                  <ChevronRight size={16} className="text-gray-300 text-sm self-center flex-shrink-0" />
                </button>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {followers.map(follower => (
                <FollowerGridCard
                  key={follower.id}
                  follower={follower}
                  onClick={() => openDetail(follower)}
                />
              ))}
            </div>
          )}
          {contactsError && !contactsLoading && (
            <button type="button" onClick={loadFollowers} className="w-full py-4 text-sm text-red-600">
              {t?.('common.requestFailed') || 'Request failed'} — {t?.('common.retry') || 'Retry'}
            </button>
          )}
          {!contactsError && (hasMore || contactsLoading) && (
            <div ref={loadMoreRef} className="py-5 text-center text-sm text-gray-400">
              {contactsLoading ? (t?.('common.loading') || 'Loading…') : null}
            </div>
          )}
        </div>
      </ResponsiveShell>

      {/* Follower detail overlay */}
      {showDetail && selectedFollower && (
        <div className="fixed inset-0 bg-[#f2f4f8] z-50 overflow-y-auto flex flex-col">

          {/* Header */}
          <div className="bg-[#1535c8] px-4 pt-4 pb-4 flex items-center gap-3">
            <button onClick={closeDetail}>
              <ArrowLeft size={20} className="text-white" />
            </button>
            <span className="text-white font-bold text-base flex-1 text-center">{t('followers.title')}</span>
            {editMode ? (
              <div className="flex items-center gap-3">
                <button onClick={cancelEdit} className="text-white text-sm font-medium opacity-75 hover:opacity-100">
                  {t?.('contacts.cancel') || 'Cancel'}
                </button>
                <button
                  onClick={saveEdit}
                  disabled={saving}
                  className="text-white text-sm font-bold disabled:opacity-50"
                >
                  {saving ? (t?.('contacts.saving') || 'Saving…') : (t?.('contacts.save') || 'Save')}
                </button>
              </div>
            ) : (
              <button onClick={startEdit} className="text-white opacity-75 hover:opacity-100">
                <Pencil size={16} />
              </button>
            )}
          </div>

          <div className="flex-1 px-4 pt-4 pb-8">
            <div className="bg-white rounded-2xl p-4 mb-4" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>

              {/* Avatar + name + channel badges — always visible */}
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 bg-[#e3eaf8] rounded-full flex items-center justify-center text-[#1535c8] text-xl font-bold flex-shrink-0">
                  {(selectedFollower.name || 'U').charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="text-base font-bold text-gray-900">{selectedFollower.name || 'Unknown'}</div>
                  <div className="flex gap-1.5 flex-wrap mt-1">
                    {getUniqueContactChannels(selectedFollower.channels).map((ch, i) => (
                      <ContactChannelBadge key={i} platform={ch.platform} />
                    ))}
                  </div>
                </div>
              </div>

              {/* Edit mode: form */}
              {editMode && editDraft && (
                <FollowerEditForm
                  draft={editDraft}
                  setDraft={setEditDraft}
                  t={t}
                  tenantId={tenantId}
                  contact={selectedFollower}
                  onTagsChange={updateSelectedFollowerTags}
                />
              )}

              {/* Read mode: all sections */}
              {!editMode && (
                <>
                  {/* Section 1: Contact Info */}
                  {hasContactInfo(selectedFollower) && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t?.('contacts.contactInfo') || 'Contact Info'}
                      </div>
                      {selectedFollower.phone   && <InfoRow label={t?.('contacts.phone')   || 'Phone'}   value={selectedFollower.phone} />}
                      {selectedFollower.email   && <InfoRow label={t?.('contacts.email')   || 'Email'}   value={selectedFollower.email} />}
                      {selectedFollower.address && <InfoRow label={t?.('contacts.address') || 'Address'} value={selectedFollower.address} />}
                      {selectedFollower.street   && <InfoRow label={t?.('contacts.street')   || 'Street'}       value={selectedFollower.street} />}
                      {selectedFollower.ward     && <InfoRow label={t?.('contacts.ward')     || 'Ward'}         value={selectedFollower.ward} />}
                      {selectedFollower.district && <InfoRow label={t?.('contacts.district') || 'District'}     value={selectedFollower.district} />}
                      {selectedFollower.province && <InfoRow label={t?.('contacts.province') || 'Province/City'} value={selectedFollower.province} />}
                      {(selectedFollower.latitude != null && selectedFollower.longitude != null) && (
                        <InfoRow
                          label={t?.('contacts.coordinates') || 'Coordinates'}
                          value={`${selectedFollower.latitude}, ${selectedFollower.longitude}`}
                        />
                      )}
                      {selectedFollower.stage   && <InfoRow label={t?.('contacts.stage')   || 'Stage'}   value={selectedFollower.stage} />}
                      <InfoRow label={t?.('contacts.gender') || 'Gender'} value={formatContactGender(selectedFollower.gender, t)} />
                      <InfoRow label={t?.('contacts.dob') || 'Date of birth'} value={formatContactDob(selectedFollower.dob, t)} />
                    </div>
                  )}

                  {/* Section 2: Channels */}
                  {getUniqueContactChannels(selectedFollower.channels).length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t?.('contacts.channels') || 'Channels'}
                      </div>
                      <div className="space-y-2">
                        {getUniqueContactChannels(selectedFollower.channels).map((ch, i) => (
                          <div key={i} className="flex items-center gap-2 text-sm">
                            <ContactChannelBadge platform={ch.platform} />
                            <span
                              className="font-mono text-xs text-gray-500 truncate max-w-[140px]"
                              title={ch.platform_id}
                            >
                              {ch.platform_id || '—'}
                            </span>
                            <span className="ml-auto text-xs text-gray-400 flex-shrink-0">
                              {formatDate(ch.followed_at)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Section 3: Activity */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {t?.('contacts.activity') || 'Activity'}
                    </div>
                    <div className="flex gap-2">
                      <FollowerStatTile
                        label={t?.('contacts.transactionCount') || 'Transactions'}
                        value={selectedFollower.transaction_count ?? 0}
                      />
                      <FollowerStatTile
                        label={t?.('contacts.totalSpent') || 'Total Spent'}
                        value={currencyResolved
                          ? formatMoney(selectedFollower.total_spent ?? 0, currency, i18n.language)
                          : <span role="status" aria-label={t('common.loading')}>—</span>}
                      />
                      <FollowerStatTile
                        label={t?.('contacts.lastTransaction') || 'Last Transaction'}
                        value={formatDate(selectedFollower.last_transaction_at)}
                      />
                    </div>
                    {(selectedFollower.follow_sources || []).length > 0 && (
                      <div className="mt-2">
                        <InfoRow
                          label={t?.('contacts.followSources') || 'Follow Sources'}
                          value={selectedFollower.follow_sources.join(', ')}
                        />
                      </div>
                    )}
                  </div>

                  <ContactTagsPanel
                    tenantId={tenantId}
                    contactId={selectedFollower.id}
                    tags={selectedFollower.tags}
                    variant="detail"
                    onTagsChange={updateSelectedFollowerTags}
                  />

                  {selectedFollower.notes ? (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                        {t?.('contacts.notes') || 'Notes'}
                      </div>
                      <InfoRow label={t?.('contacts.notes') || 'Notes'} value={selectedFollower.notes} />
                    </div>
                  ) : null}

                  {/* Section 5: Linked Conversations */}
                  <div className="mt-4 pt-4 border-t border-gray-100">
                    <div className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">
                      {t?.('contacts.conversations') || 'Conversations'}
                    </div>
                  </div>

                  {/* Message button */}
                  <div className="mt-4">
                    <button
                      className="w-full border border-[#1535c8] text-[#1535c8] rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold"
                      onClick={() => {
                        closeDetail()
                        if (!tenantId) return
                        const userId = selectedFollower.channels?.[0]?.platform_id || null
                        if (userId) {
                          navigate(`${prefix}/inbox?user=${userId}`)
                        } else {
                          navigate(`${prefix}/inbox?search=${encodeURIComponent(selectedFollower.name || '')}`)
                        }
                      }}
                    >
                      <MessageCircle size={16} />
                      {t('customers.message')}
                    </button>
                  </div>

                  <div className="mt-3">
                    <button
                      className="w-full border border-red-200 text-red-600 rounded-xl py-3 flex items-center justify-center gap-2 text-sm font-semibold hover:bg-red-50 disabled:opacity-50 disabled:hover:bg-transparent"
                      onClick={() => setDeleteConfirmOpen(true)}
                      disabled={deleting}
                    >
                      <Trash2 size={16} />
                      {deleting
                        ? (t?.('followers.deleting') || 'Deleting…')
                        : (t?.('followers.delete') || 'Delete follower')}
                    </button>
                  </div>
                </>
              )}
            </div>

            <ContactBookingHistory
              tenantId={tenantId}
              contactId={selectedFollower.id}
              bookingSummary={selectedFollower.booking_summary}
              summaryLoading={contactDetailLoading}
              canViewHistory={canViewBookingHistory}
            />
          </div>
        </div>
      )}

      {deleteConfirmOpen && selectedFollower && (
        <div
          className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleting) setDeleteConfirmOpen(false)
          }}
        >
          <div className="w-full max-w-sm bg-white rounded-xl p-4 shadow-2xl border border-slate-100">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-red-50 text-red-600 flex items-center justify-center flex-shrink-0">
                <Trash2 size={18} />
              </div>
              <div className="min-w-0">
                <div className="text-base font-bold text-gray-900">
                  {t?.('followers.delete') || 'Delete follower'}
                </div>
                <div className="text-sm text-gray-500 mt-1 leading-relaxed">
                  {t?.('followers.deleteConfirm') || 'Delete this follower permanently?'}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-5">
              <button
                type="button"
                onClick={() => setDeleteConfirmOpen(false)}
                disabled={deleting}
                className="border border-slate-200 text-slate-600 rounded-lg px-4 py-2 text-sm font-semibold hover:bg-slate-50 disabled:opacity-60"
              >
                {t?.('common.cancel') || 'Cancel'}
              </button>
              <button
                type="button"
                onClick={deleteFollower}
                disabled={deleting}
                className="bg-red-600 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-red-700 disabled:opacity-70"
              >
                {deleting
                  ? (t?.('followers.deleting') || 'Deleting…')
                  : (t?.('common.delete') || 'Delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      <ImportContactsModal
        open={importOpen}
        onClose={() => setImportOpen(false)}
        tenantId={tenantId}
        defaultStage="follower"
        onImported={loadFollowers}
      />
    </>
  )
}
